import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Panel, Dialog, TextInput, Button, Notice, IconButton, ListItem } from '@origo/origo-ui';
import { MagnifyingGlassPlusIcon } from '@phosphor-icons/react';
import {
    useSeksjoner,
    useSaveSeksjon,
    useSeksjonClient,
} from '@origo/seksjon';
import type { Område } from '@origo/seksjon';
import { useMapViewContext, selectedPolygonSymbol } from '@origo/map-context';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type { DelområdegruppeView } from './NavPanel';
import { formatAreal } from '../../planer/utils/planUtils';
import { resolveTargetParentGlobalId } from './veiledningKopiUtils';
import styles from './KonverterTilDelområdePanel.module.css';

/** Metadata om kilden som ble kopiert — brukes til toast + angre etter kopiering. */
export interface CopySource {
    id: string;
    navn: string;
    sectionCount: number;
    copiedGlobalIds: string[];
    copiedObjectIds: number[];
}

/**
 * Target for konvertering. `delomrade` kopierer til et FKB-delområde, `gruppe`
 * kopierer til en delområdegruppe, `delomradetype` kopierer til en delområdetype.
 * Kopi-semantikk: kilde-seksjoner, kilde-elementer og kilde-området forblir
 * intakte. Saksbehandler verifiserer og sletter kilden manuelt.
 */
export type ConvertTarget =
    | { kind: 'delomrade'; id: string; navn: string; delomradetype: string; copiedFrom: CopySource }
    | { kind: 'gruppe'; gruppeId: string; navn: string; delomradetype: string; copiedFrom: CopySource }
    | { kind: 'delomradetype'; id: string; navn: string; copiedFrom: CopySource };

interface Props {
    egendefinertOmråde: Område;
    planId: string;
    /** FKB delområde-tre (delomradetype → children[]). */
    delomradetypeAreas: Område[];
    delområdegrupper: readonly DelområdegruppeView[];
    onConvert: (target: ConvertTarget) => void;
    onClose: () => void;
}

type PendingTarget =
    | { kind: 'delomrade'; id: string; navn: string; delomradetype: string }
    | { kind: 'gruppe'; gruppeId: string; navn: string; delomradetype: string }
    | { kind: 'delomradetype'; id: string; navn: string };

const TALL_TEKST = ['null', 'ett', 'to', 'tre', 'fire', 'fem', 'seks', 'sju', 'åtte', 'ni'];
function tallTekst(n: number, capitalize = false): string {
    const t = n < 10 ? TALL_TEKST[n] : String(n);
    return capitalize ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

const HIGHLIGHT_LAYER_ID = 'konverter-omrade-highlight';

export function KonverterTilDelområdePanel({
    egendefinertOmråde,
    planId,
    delomradetypeAreas,
    delområdegrupper,
    onConvert,
    onClose,
}: Readonly<Props>) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [target, setTarget] = useState<PendingTarget | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [highlightedIds, setHighlightedIds] = useState<ReadonlySet<string>>(new Set());
    const [ready, setReady] = useState(false);

    const highlightLayerRef = useRef<GraphicsLayer | null>(null);

    const { mapView } = useMapViewContext();

    const saveMutation = useSaveSeksjon();
    const seksjonClient = useSeksjonClient();

    // Last seksjoner fra kilde (egendef-området) — alltid.
    // includeOverriddenAncestors: vi må walke parentGlobalId-kjeden i
    // resolveTargetParentGlobalId for å finne in-scope ancestor, og default-
    // filteret fjerner ancestors som er overstyrt av sine barn. Uten denne
    // flaggen ville override-status gått tapt ved kopiering.
    const kildeSections = useSeksjoner(
        { type: "Område", planId, områdeId: egendefinertOmråde.id },
        { includeOverriddenAncestors: true }
    );

    // Last seksjoner fra target — kontekst varierer med target-kind.
    const targetSections = useSeksjoner(
        target
            ? target.kind === 'gruppe'
                ? {
                    type: 'Delområdegruppe',
                    planId,
                    gruppeId: target.gruppeId,
                    delområdetype: target.delomradetype,
                }
                : target.kind === 'delomradetype'
                    ? {
                        type: 'Delområdetype',
                        planId,
                        delområdetype: target.id,
                    }
                    : {
                        type: 'Delområde',
                        planId,
                        områdeId: target.id,
                        delområdetype: target.delomradetype,
                    }
            : null
    );

    const allCandidates = useMemo(
        () => delomradetypeAreas.flatMap((g) => g.children ?? []),
        [delomradetypeAreas]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return delomradetypeAreas
            .map((group) => {
                const typeMatches = !q || group.navn.toLowerCase().includes(q);
                const childMatches = (group.children ?? []).filter((child) => {
                    if (!q) return true;
                    return (
                        child.navn.toLowerCase().includes(q) ||
                        (child.code ?? '').toLowerCase().includes(q)
                    );
                });
                const gruppeMatches = delområdegrupper
                    .filter((g) => g.delomradetype === group.id)
                    .filter((g) => !q || g.navn.toLowerCase().includes(q));
                return { group, typeMatches, children: childMatches, grupper: gruppeMatches };
            })
            .filter((g) => g.typeMatches || g.children.length > 0 || g.grupper.length > 0);
    }, [delomradetypeAreas, delområdegrupper, search]);

    // ── GraphicsLayer — livssyklus følger mapView ──
    useEffect(() => {
        const map = mapView?.map;
        if (!mapView || !map) return;

        let destroyed = false;
        const layer = new GraphicsLayer({ id: HIGHLIGHT_LAYER_ID, listMode: 'hide' });
        map.add(layer);
        highlightLayerRef.current = layer;

        mapView.when(() => {
            if (!destroyed) setReady(true);
        });

        return () => {
            destroyed = true;
            setReady(false);
            map.remove(layer);
            layer.destroy();
            highlightLayerRef.current = null;
        };
    }, [mapView]);

    // ── Tegn markering for delomrader ──
    useEffect(() => {
        const layer = highlightLayerRef.current;
        if (!layer || !ready) return;

        layer.removeAll();

        const [r, g, b] = selectedPolygonSymbol().color;

        for (const d of allCandidates) {
            if (!d.geometry) continue;
            const isActive = highlightedIds.size > 0
                ? highlightedIds.has(d.id)
                : target?.kind === 'delomrade' && target.id === d.id;
            layer.add(new Graphic({
                geometry: d.geometry,
                symbol: (isActive
                    ? {
                        type: 'simple-fill' as const,
                        color: [r, g, b, 0.4],
                        outline: { color: [r, g, b, 1], width: 2 },
                    }
                    : {
                        type: 'simple-fill' as const,
                        color: [r, g, b, 0],
                        outline: { color: [r, g, b, 0.4], width: 1 },
                    }) as never,
            }));
        }
    }, [highlightedIds, target, allCandidates, ready]);

    const handleSelectDelomradetype = useCallback((group: Område) => {
        setHighlightedIds(new Set());
        setTarget(prev =>
            prev?.kind === 'delomradetype' && prev.id === group.id ? null
                : { kind: 'delomradetype', id: group.id, navn: group.navn }
        );
    }, []);

    const handleSelectChildTarget = useCallback((group: Område, child: Område) => {
        setHighlightedIds(new Set());
        const label = child.code ?? child.navn;
        setTarget(prev =>
            prev?.kind === 'delomrade' && prev.id === child.id ? null
                : { kind: 'delomrade', id: child.id, navn: label, delomradetype: group.id }
        );
    }, []);

    const handleSelectGruppeTarget = useCallback((gruppe: DelområdegruppeView) => {
        setHighlightedIds(new Set());
        setTarget(prev =>
            prev?.kind === 'gruppe' && prev.gruppeId === gruppe.id ? null
                : { kind: 'gruppe', gruppeId: gruppe.id, navn: gruppe.navn, delomradetype: gruppe.delomradetype }
        );
    }, []);

    const handleNext = useCallback(() => setConfirmOpen(true), []);

    const handleBack = useCallback(() => {
        setConfirmOpen(false);
        setError(null);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!target) return;
        setBusy(true);
        setError(null);
        try {
            const targetSeksjonstype =
                target.kind === 'gruppe' ? 'Delområdegruppe' :
                    target.kind === 'delomradetype' ? 'Delområdetype' :
                        'Delområde';
            const targetOmrådeId =
                target.kind === 'gruppe' ? target.gruppeId :
                    target.kind === 'delomradetype' ? target.id :
                        target.id;
            const targetDelomradetype =
                target.kind === 'delomradetype' ? target.id :
                    target.delomradetype;

            // useSections returnerer ALT som vises i konteksten — inkludert
            // arvede seksjoner fra Delområdetype/Plan/Kommune. Vi må kun
            // kopiere seksjoner som er DIREKTE eid av kilden. Arvede seksjoner
            // røres ikke (de tilhører høyere nivåer og brukes av søsken).
            const kilde = (kildeSections.data ?? []).filter(
                (s) =>
                    !s.isInherited &&
                    s.seksjonstype === "Område" &&
                    s.områdeId === egendefinertOmråde.id
            );

            // Ren kopi-operasjon: ADD nye seksjoner på target, ADD nye elementer
            // på target med samme filId. Kilde-seksjoner, kilde-elementer og
            // kilde-området røres IKKE. Saksbehandler verifiserer og sletter
            // kilden manuelt etterpå.
            //
            // Override-kjeden re-peker mot første ancestor i kilden som faktisk
            // vil være in-scope i target-konteksten (se resolveTargetParentGlobalId).
            // Hvis ingen slik ancestor finnes, faller vi tilbake til plain direct.
            //
            // Per-rad try/catch så vi får logget hvilke seksjoner som
            // eventuelt feiler.
            const elementRepo = seksjonClient.elementer;
            const addFailures: { tittel: string; error: string }[] = [];
            const copiedGlobalIds: string[] = [];
            const copiedObjectIds: number[] = [];
            let successCount = 0;
            for (const s of kilde) {
                try {
                    const newParentGlobalId = s.overstyrer
                        ? resolveTargetParentGlobalId(
                            s.parentGlobalId,
                            kildeSections.data ?? [],
                            target.kind
                        )
                        : undefined;
                    const preserveOverride = s.overstyrer && !!newParentGlobalId;

                    const newSeksjon = await saveMutation.mutateAsync({
                        values: {
                            seksjonstype: targetSeksjonstype,
                            planId,
                            områdeId: targetOmrådeId,
                            delområdetype: targetDelomradetype,
                            områdeNavn: target.navn,
                            tittel: s.tittel,
                            tekst: s.tekst,
                            synlig: s.synlig,
                            kommunal: s.kommunal,
                            infoboks: s.infoboks,
                            topp: s.topp,
                            kunUregulert: s.kunUregulert,
                            prioritet: s.prioritet,
                            overstyrer: preserveOverride,
                            parentGlobalId: preserveOverride ? newParentGlobalId : undefined,
                        },
                    });

                    // Kopier illustrasjoner: ADD nye element-rader pekende på
                    // ny seksjon-globalId. filId deles mellom kilde og kopi —
                    // selve blob i lagring trenger ikke kopieres.
                    if (elementRepo && s.globalId && newSeksjon.globalId) {
                        const sourceElements = await elementRepo.fetchForSection(s.globalId);
                        for (const el of sourceElements) {
                            await elementRepo.add({
                                seksjonsGlobalId: newSeksjon.globalId,
                                filId: el.filId,
                                galleriElementId: el.galleriElementId,
                                tittel: el.tittel,
                                beskrivelse: el.beskrivelse,
                                prioritet: el.prioritet,
                            });
                        }
                    }

                    if (newSeksjon.globalId) copiedGlobalIds.push(newSeksjon.globalId);
                    if (newSeksjon.objectId != null) copiedObjectIds.push(newSeksjon.objectId);
                    successCount++;
                } catch (saveErr) {
                    const msg = (saveErr as Error)?.message ?? String(saveErr);
                    addFailures.push({ tittel: s.tittel, error: msg });
                    // eslint-disable-next-line no-console
                    console.error('[Konverter] kopiering feilet for', s.tittel, saveErr);
                }
            }
            if (addFailures.length > 0) {
                throw new Error(
                    `${addFailures.length} av ${kilde.length} veiledningspunkt kunne ikke kopieres: ${addFailures
                        .map((f) => `"${f.tittel}" (${f.error})`)
                        .join(', ')}`
                );
            }

            const copiedFrom: CopySource = {
                id: egendefinertOmråde.id,
                navn: egendefinertOmråde.navn,
                sectionCount: successCount,
                copiedGlobalIds,
                copiedObjectIds,
            };

            onConvert(
                target.kind === 'gruppe'
                    ? { kind: 'gruppe', gruppeId: target.gruppeId, navn: target.navn, delomradetype: target.delomradetype, copiedFrom }
                    : target.kind === 'delomradetype'
                        ? { kind: 'delomradetype', id: target.id, navn: target.navn, copiedFrom }
                        : { kind: 'delomrade', id: target.id, navn: target.navn, delomradetype: target.delomradetype, copiedFrom }
            );
        } catch (e) {
            setError((e as Error)?.message ?? 'Kunne ikke konvertere');
            setBusy(false);
        }
    }, [
        target,
        kildeSections.data,
        saveMutation,
        planId,
        egendefinertOmråde.id,
        egendefinertOmråde.navn,
        seksjonClient,
        onConvert,
    ]);

    const zoomTo = useCallback((delområde: Område) => {
        if (!mapView || !delområde.geometry) return;
        setHighlightedIds(new Set([delområde.id]));
        const extent = delområde.geometry.extent;
        const goToTarget = extent ? extent.clone().expand(1.6) : delområde.geometry;
        mapView.goTo(goToTarget, { duration: 400, easing: 'ease-out' }).catch(() => { });
    }, [mapView]);

    const zoomToType = useCallback((group: Område) => {
        const children = (group.children ?? []).filter((c) => c.geometry);
        if (!mapView || children.length === 0) return;
        setHighlightedIds(new Set(children.map((c) => c.id)));
        mapView.goTo(children.map((c) => c.geometry!), { duration: 400, easing: 'ease-out' }).catch(() => { });
    }, [mapView]);

    // Tell kun DIREKTE seksjoner — arvede seksjoner fra Delområdetype/Plan/
    // Kommune skal ikke påvirkes av konverteringen.
    const kildeCount = useMemo(
        () =>
            (kildeSections.data ?? []).filter(
                (s) =>
                    !s.isInherited &&
                    s.seksjonstype === "Område" &&
                    s.områdeId === egendefinertOmråde.id
            ).length,
        [kildeSections.data, egendefinertOmråde.id]
    );
    const targetCount = useMemo(() => {
        if (!target) return 0;
        if (target.kind === 'delomradetype') {
            return (targetSections.data ?? []).filter(
                (s) => !s.isInherited && s.seksjonstype === 'Delområdetype' && s.delområdetype === target.id
            ).length;
        }
        const expectedType = target.kind === 'gruppe' ? 'Delområdegruppe' : 'Delområde';
        const expectedOmrådeId = target.kind === 'gruppe' ? target.gruppeId : target.id;
        return (targetSections.data ?? []).filter(
            (s) =>
                !s.isInherited &&
                s.seksjonstype === expectedType &&
                s.områdeId === expectedOmrådeId
        ).length;
    }, [targetSections.data, target]);
    const hasExisting = targetCount > 0;

    const targetKindLabel =
        target?.kind === 'gruppe' ? 'delområdegruppen' :
            target?.kind === 'delomradetype' ? 'delområdetypen' :
                'delområdet';

    const footer = (
        <>
            <Button variant="secondary" onClick={onClose}>Avbryt</Button>
            <Button variant="primary" onClick={handleNext} disabled={!target}>Neste</Button>
        </>
    );

    return (
        <>
            <Panel heading="Flytt bare veiledningene til et delområde" closable onClose={onClose} flush footer={footer}>
                <div className={styles.body}>
                    <p className={styles.intro}>
                        Velg delområdet eller delområdegruppen som "{egendefinertOmråde.navn}" tilsvarer.
                        Veiledningspunktene kopieres over. Det egendefinerte området beholdes så du kan verifisere — slett det manuelt etterpå.
                    </p>
                    <div className={styles.search}>
                        <TextInput
                            label="Søk delområde eller gruppe"
                            placeholder="Filtrer på navn eller kode"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className={styles.list}>
                        {filtered.length === 0 ? (
                            <p className={styles.empty}>Ingen delområder eller grupper matcher søket.</p>
                        ) : (
                            (() => {
                                return filtered.map(({ group, typeMatches, children, grupper }) => {
                                    const isTypeSelected = target?.kind === 'delomradetype' && target.id === group.id;
                                    return (
                                        <div key={group.id} className={styles.group}>
                                            {typeMatches && (
                                                <div className={[
                                                    styles.optionWrap,
                                                    isTypeSelected ? styles.optionSelected : '',
                                                ].filter(Boolean).join(' ')}>
                                                    <ListItem
                                                        leading={
                                                            <input
                                                                type="radio"
                                                                aria-label="Velg"
                                                                checked={isTypeSelected}
                                                                onChange={() => { }}
                                                                tabIndex={-1}
                                                                className={styles.radioInput}
                                                            />
                                                        }
                                                        onClick={() => handleSelectDelomradetype(group)}
                                                        className={styles.typeHeader}
                                                    >
                                                        {group.navn}
                                                    </ListItem>
                                                    <IconButton
                                                        className={styles.zoomButton}
                                                        icon={<MagnifyingGlassPlusIcon size={14} />}
                                                        label={`Zoom til ${group.navn}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => zoomToType(group)}
                                                        disabled={!(group.children ?? []).some((c) => c.geometry)}
                                                    />
                                                </div>
                                            )}
                                            {grupper.map((g) => {
                                                const isGruppeSelected = target?.kind === 'gruppe' && target.gruppeId === g.id;
                                                return (
                                                    <div
                                                        key={`gruppe-${g.id}`}
                                                        className={[
                                                            styles.optionRow,
                                                            isGruppeSelected ? styles.optionSelected : '',
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        <ListItem
                                                            leading={
                                                                <input
                                                                    type="radio"
                                                                    aria-label="Velg"
                                                                    checked={isGruppeSelected}
                                                                    onChange={() => { }}
                                                                    tabIndex={-1}
                                                                    className={styles.radioInput}
                                                                />
                                                            }
                                                            onClick={() => handleSelectGruppeTarget(g)}
                                                            className={styles.optionItem}
                                                        >
                                                            {g.navn}
                                                        </ListItem>
                                                    </div>
                                                );
                                            })}
                                            {children.map((child) => {
                                                const isChildSelected = target?.kind === 'delomrade' && target.id === child.id;
                                                const areal = formatAreal(child.areaM2);
                                                return (
                                                    <div
                                                        key={child.id}
                                                        className={[
                                                            styles.optionRow,
                                                            isChildSelected ? styles.optionSelected : '',
                                                        ].filter(Boolean).join(' ')}
                                                    >
                                                        <ListItem
                                                            leading={
                                                                <input
                                                                    type="radio"
                                                                    aria-label="Velg"
                                                                    checked={isChildSelected}
                                                                    onChange={() => { }}
                                                                    tabIndex={-1}
                                                                    className={styles.radioInput}
                                                                />
                                                            }
                                                            onClick={() => handleSelectChildTarget(group, child)}
                                                            className={styles.optionItem}
                                                        >
                                                            {child.code && <span className={styles.optionCode}>{child.code}</span>}
                                                            {child.navn}
                                                            {areal && <span className={styles.areal}>({areal})</span>}
                                                        </ListItem>
                                                        <IconButton
                                                            className={styles.zoomButton}
                                                            icon={<MagnifyingGlassPlusIcon size={14} />}
                                                            label={`Zoom til ${child.navn}`}
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => zoomTo(child)}
                                                            disabled={!child.geometry}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                });
                            })()
                        )}
                    </div>
                </div>
            </Panel>
            <Dialog
                open={confirmOpen}
                onClose={handleBack}
                title="Flytt veiledningene"
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={handleBack} disabled={busy}>Tilbake</Button>
                        <Button
                            variant="primary"
                            onClick={handleConfirm}
                            disabled={busy || targetSections.isLoading || kildeSections.isLoading}
                        >
                            {busy ? 'Kopierer…' : 'Flytt'}
                        </Button>
                    </div>
                }
            >
                <div className={styles.confirmBody}>
                    {hasExisting ? (
                        <Notice variant="warning">
                            {target?.kind === 'gruppe' ? 'Delområdegruppen' : target?.kind === 'delomradetype' ? 'Delområdetypen' : 'Delområdet'}{' '}
                            <strong>{target?.navn}</strong> har allerede {tallTekst(targetCount)} veiledningspunkt
                            {targetCount === 1 ? '' : 'er'}. De {kildeCount} nye legges til i tillegg — eventuelle duplikater må ryddes manuelt etterpå.
                        </Notice>
                    ) : null}
                    <p className={styles.confirmText}>
                        {tallTekst(kildeCount, true)} veiledningspunkt{kildeCount === 1 ? '' : 'er'} kopieres fra{' '}
                        <span className={styles.confirmTarget}>"{egendefinertOmråde.navn}"</span>{' '}
                        til {targetKindLabel}{' '}
                        <span className={styles.confirmTarget}>"{target?.navn}"</span>.
                        Det egendefinerte området beholdes — du kan slette det manuelt etter at du har verifisert kopien.
                    </p>
                    {error && <p className={styles.error}>{error}</p>}
                </div>
            </Dialog>
        </>
    );
}
